<template>
  <div class="quiz">
    <div v-if="loading" class="quiz-loader">
      <svg viewBox="0 0 50 50">
        <circle cx="25" cy="25" r="20" fill="none" stroke-width="5" stroke-linecap="round" stroke-dasharray="360"/>
        <circle cx="25" cy="25" r="20" fill="none" stroke-width="5" stroke-linecap="round" stroke-dasharray="90" stroke-dashoffset="60"/>
      </svg>
    </div>

    <div v-else-if="templates[quizKey]" class="quiz-wrap">
      <div v-if="activeStep + 1 !== steps.length" class="quiz-nav">
        <div class="quiz-counter">
          {{ activeStep + 1}}/{{ steps.length }}
        </div>
        <button v-if="activeStep" type="button" class="quiz-back" @click="prevStep()" />
        <div class="quiz-line">
          <div :style="`width: ${progress}%`" />
        </div>
      </div>

      <template v-for="(step, index) in steps">
        <Transition>
          <div v-show="index === activeStep" class="quiz-step">
            <div v-if="step.isPhone || step.isCode" class="quiz-safety">
              <img src="@/assets/img/safety.svg" alt="">
            </div>
            <p class="quiz-title" :class="{'quiz-title--form': step.isForm || step.isCode}">
              <span v-html="step.title" /> <span v-if="step.isCode" class="nowrap">{{ phone }}</span>
            </p>
            <p v-if="step.subtitle" class="quiz-subtitle">
              {{ step.subtitle }}
            </p>
            <StepOptions
              v-if="step.options && step.options.length"
              :step-name="step.name"
              :options="step.options"
              :is-column="step.isColumn"
              :multiple="step.multiple"
              :with-other="step.withOther"
              :answer="quizAnswers[step.name]"
              @select="selectOption(step.name, $event)"
            />
            <StepRange
              v-if="step.min && step.max"
              :min="step.min"
              :max="step.max"
              :unit="step.unit"
              :skip="step.skip"
              @next="quizAnswers[step.name] = $event, nextStep()"
            />
            <StepInput
              v-if="step.isInput"
              :placeholder="step.placeholder"
              :skip="step.skip"
              :unit="step.unit"
              @next="quizAnswers[step.name] = $event, nextStep()"
            />
            <StepTextarea
              v-if="step.isTextarea"
              :skip="step.skip"
              :label="step.label"
              :placeholder="step.placeholder"
              @next="quizAnswers[step.name] = $event, nextStep()"
            />
            <StepForm
              v-if="step.isForm"
              @next="contactsFilled($event)"
            />
            <StepPhone
              v-if="step.isPhone"
              :sendCodeError="sendCodeError"
              :sendCodeDisable="sendCodeDisable || !!timer"
              :timer="timer"
              @input="sendCodeError = false"
              @send="phone = $event, sendCode()"
            />
            <StepCode
              v-if="step.isCode"
              :uuid="quizSettings['web-form-uuid']"
              :token="quizSettings.token"
              :timer="timer"
              @resend="sendCode('resend')"
              @next="nextStep(), sendDataLayer('quiz_code_step_passed')"
              @prev="prevStep()"
            />
            <StepFinish v-if="step.isFinish" />
          </div>
        </Transition>
      </template>

      <Transition>
        <KeepGoing v-show="showKeepGoing" />
      </Transition>

      <Transition name="fade">
        <div
          v-if="showAbout"
          class="quiz-backdrop"
          @click="showAbout = false"
        />
      </Transition>
      <Transition name="slideup">
        <div v-if="showAbout" class="quiz-about">
          <button type="button" @click="showAbout = false">
            <img src="@/assets/img/close.svg" alt="close">
          </button>
          <div class="quiz-about__header">
            A1 SolarStore marketplace + services in one place
          </div>
          <p>One platform for buying and getting the job done</p>
        </div>
      </Transition>

      <div class="quiz-footer">
        Powered by <img height="9" src="@/assets/img/logo-a1.svg" alt="" @click="showAbout = true">
      </div>
    </div>

    <div v-else class="quiz-incorrect">UUID incorrect</div>
  </div>
</template>

<script>
import tzLookup from 'tz-lookup'
import { templates } from "./templates.js"
import KeepGoing from './components/KeepGoing.vue'
import StepOptions from './components/StepOptions.vue'
import StepInput from './components/StepInput.vue'
import StepTextarea from './components/StepTextarea.vue'
import StepRange from './components/StepRange.vue'
import StepForm from './components/StepForm.vue'
import StepPhone from './components/StepPhone.vue'
import StepCode from './components/StepCode.vue'
import StepFinish from './components/StepFinish.vue'
import * as Sentry from '@sentry/browser'

export default {
  components: {
    KeepGoing,
    StepOptions,
    StepInput,
    StepTextarea,
    StepRange,
    StepForm,
    StepPhone,
    StepCode,
    StepFinish,
  },
  data() {
    return {
      apiUrl: import.meta.env.VITE_API_URL,
      templates,
      quizEnd: [
        {
          title: 'How many quotes would you like to receive?',
          name: 'max_relationship_amount',
          options: [
            { value: 1 },
            { value: 3 },
            { value: 5 },
            { value: 10 },
            { label: 'No limit', value: 100 },
          ],
          isColumn: true,
        },
        {
          title: 'You`re almost there! Please enter your contact information',
          isForm: true,
        },
        {
          title: 'Enter your phone number',
          subtitle: 'We need to confirm that you`re a real person. Your data is safe with us - we won’t share it with third parties',
          isPhone: true
        },
        {
          title: `Enter one-time code we’ve just sent to`,
          isCode: true
        },
        {
          title: 'Preferred contact method?',
          name: 'preferred_contact_method',
          options: [
            { img: 'email', value: 'email' },
            { img: 'phone', value: 'phone' },
          ],
        },
        {
          title: 'Request submitted',
          subtitle: 'You`ll soon start getting contractor offers. We`ll notify you via SMS and email.',
          isFinish: true
        },
      ],
      loading: false,
      showKeepGoing: false,
      showAbout: false,
      activeStep: 0,
      quizSettings: null,
      quizKey: null,
      quizAnswers: null,
      fname: '',
      lname: '',
      email: '',
      phone: '',
      sendCodeDisable: false,
      sendCodeError: false,
      location: null,
      fingerprint: null,
      timer: 0,
      timerInterval: null,
      finishQuizDisable: false,
      // GTM метки:
      source_url: null, // Удалить позже, будет переименовано в quiz_domain
      quiz_domain: null,
      quiz_url: null,
      quiz_placement: null
    }
  },
  computed: {
    steps() {
      let steps = []
      if (this.quizKey) steps = this.templates[this.quizKey] || []
      return [...steps, ...this.quizEnd]
    },
    progress() {
      const n = (this.activeStep / this.steps.length) * 100
      return n.toFixed()
    },
  },
  watch: {
    activeStep() {
      this.sendHeight()
    }
  },
  created() {
    this.fetchData()
  },
  mounted() {
    window.addEventListener('load', this.sendHeight())
    window.addEventListener('resize', this.sendHeight())
    const params = new URLSearchParams(window.location.search)
    this.source_url = params.get('source_url')
    this.quiz_domain = params.get('quiz_domain')
    this.quiz_url = params.get('quiz_url')
    this.quiz_placement = params.get('quiz_placement')
  },
  methods: {
    sendHeight() {
      setTimeout(() => {
        const height = document.documentElement.scrollHeight
        window.parent?.postMessage({ type: 'iframeHeight', height }, '*')
      }, 100)
    },
    async fetchData() {
      this.loading = true
      const params = new URLSearchParams(window.location.search)
      const uuid = params.get('uuid')
      try {
        if (uuid) {
          const response = await fetch(`${this.apiUrl}/leads-center/partner/web-forms/${uuid}`, { method: 'POST' })
          if (!response.ok) {
            const error = new Error()
            error.name = `[App.vue] fetchData ${response?.status}`
            error.status = response?.status
            const text = await response.text()
            error.message = text
            throw error
          }
          const data = await response.json()
          if (data?.content?.data) {
            this.quizSettings = data.content.data
            // Получаем ключ для шаблона
            const schema = JSON.parse(data.content.data.form['generator-schema'])
            if (schema) this.quizKey = schema['form-type']
            // Собираем объект для ответов
            if (this.templates[this.quizKey]) {
              const answers = {}
              this.templates[this.quizKey].forEach(option => {
                if (option.name) {
                  answers[option.name] = null
                  // Если вопрос - число
                  if (option.min) {
                    answers[option.name] = option.unit ? `${option.min} ${option.unit}` : option.min
                  }
                }
              })
              this.quizAnswers = answers
            }
            this.sendDataLayer('quiz_start')
          } else throw new Error('[App.vue] fetchData: data.content')
        }
      } catch (error) {
        let msg = {}
        try {
          msg = JSON.parse(error?.message)
        } catch (parseError) {
          msg = { raw: error?.message }
        }
        if (msg?.status?.client_code || msg?.status?.message) error.message = `Client code ${msg?.status?.client_code}: ${msg?.status?.message}`
        else if (msg.message) error.message = msg.message
        Sentry.captureException(error, {
          extra: {
            uuid,
            response: msg
          }
        })
      } finally {
        this.loading = false
      }
    },
    startTimer() {
      if (this.timer) return
      this.timer = 60
      this.timerInterval = setInterval(() => {
        if (this.timer === 0) {
          clearInterval(this.timerInterval)
          return
        }
        this.timer -= 1
      }, 1000)
    },
    async sendCode(resend) {
      try {
        this.sendCodeDisable = true
        this.sendCodeError = false
        const response = await fetch(`${this.apiUrl}/leads-center/partner/web-forms/${this.quizSettings['web-form-uuid']}/sessions/${this.quizSettings.token}/phone/send-verification-code`, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            web_form_uuid: this.quizSettings['web-form-uuid'],
            web_form_session_token: this.quizSettings.token,
            phone: this.phone.replace(/[^+\d]/g, '')
          })
        })
        if (response.ok) {
          this.startTimer()
          if (!resend) {
            this.nextStep()
            this.sendDataLayer('quiz_phone_step_passed')
          }
        } else {
          const error = new Error()
          error.name = `[App.vue] sendCode ${response?.status}`
          error.status = response?.status
          const text = await response.text()
          error.message = text
          throw error
        }
      } catch (error) {
        this.sendCodeError = true
        let msg = {}
        try {
          msg = JSON.parse(error?.message)
        } catch (parseError) {
          msg = { raw: error?.message }
        }
        if (msg?.status?.client_code || msg?.status?.message) error.message = `Client code ${msg?.status?.client_code}: ${msg?.status?.message}`
        else if (msg.message) error.message = msg.message
        Sentry.captureException(error, {
          extra: {
            web_form_uuid: this.quizSettings['web-form-uuid'],
            web_form_session_token: this.quizSettings.token,
            phone: this.phone.replace(/[^+\d]/g, ''),
            response: msg
          }
        })
      } finally {
        this.sendCodeDisable = false
      }
    },
    async finishQuiz() {
      if (this.finishQuizDisable) return
      this.finishQuizDisable = true
      const uAgent = navigator?.userAgent || ''
      this.fingerprint = {
          requestId: `${Date.now()}.${Math.random().toString(36).substr(2, 6)}`,
          browserName: uAgent.includes('Chrome') ? 'Chrome'
            : uAgent.includes('Firefox') ? 'Firefox'
            : uAgent.includes('Safari') && !uAgent.includes('Chrome') ? 'Safari'
            : uAgent.includes('Edge') ? 'Edge': 'Other'
          ,
          browserVersion: uAgent.match(/(Chrome|Firefox|Safari|Edge)\/([\d.]+)/) ? uAgent.match(/(Chrome|Firefox|Safari|Edge)\/([\d.]+)/)[2] : 'Unknow',
          confidence: { revision: 'v1.1', score: 1 },
          device: /Mobi|Android|iPhone|iPad|iPod/i.test(uAgent) ? 'Mobile' : 'Other',
          firstSeenAt: {
            global: new Date().toISOString(),
            subscription: new Date().toISOString()
          },
          incognito: false,
          ip: '',
          lastSeenAt: {
            global: new Date().toISOString(),
            subscription: new Date().toISOString()
          },
          os: uAgent.includes('Windows') ? 'Windows'
            : uAgent.includes('Mac OS') ? 'Mac OS'
            : uAgent.includes('Linux') ? 'Linux'
            : uAgent.includes('Android') ? 'Android'
            : uAgent.includes('iOS') ? 'iOS' : 'Other',
          osVersion: uAgent.match(/Windows NT ([\d.]+)/) ? uAgent.match(/Windows NT ([\d.]+)/)[1]
            : uAgent.match(/Mac OS X ([\d_]+)/) ? uAgent.match(/Mac OS X ([\d_]+)/)[1].replace(/_/g, '.') : 'Unknown',
          visitorFound: false,
          cacheHit: false
      }
      let tzByLocation = null
      if (this.location?.location?.y, this.location?.location?.x) {
        tzByLocation = tzLookup(this.location?.location?.y, this.location?.location?.x)
      }
      const additional_fields = JSON.parse(JSON.stringify(this.quizAnswers))
      let answers = []
      const formatValue = (value) => {
        let text = value.replace(/-/g, ' ')
        return text[0].toUpperCase() + text.slice(1)
      }
      Object.entries(additional_fields).forEach(a => {
        if (a[0] !== 'max_relationship_amount' && a[0] !== 'preferred_contact_method') {
          answers.push({ name: formatValue(a[0]), value: `${a[1]}` })
        }
      })
      try {
        const response = await fetch(`${this.apiUrl}/leads-center/partner/web-forms/${this.quizSettings['web-form-uuid']}/sessions/${this.quizSettings.token}`, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            web_form_uuid: this.quizSettings['web-form-uuid'],
            web_form_session_token: this.quizSettings.token,
            phone: this.phone.replace(/[^+\d]/g, ''),
            email: this.email,
            first_name: this.fname,
            last_name: this.lname,
            max_relationship_amount: this.quizAnswers['max_relationship_amount'],
            preferred_contact_method: this.quizAnswers['preferred_contact_method'],
            geo_place: this.location,
            iana_timezone: tzByLocation ? tzByLocation : Intl.DateTimeFormat().resolvedOptions().timeZone,
            form_submission_source_url: this.source_url ? `https://${this.source_url}` : window.location.href,
            session_fingerprint: this.fingerprint,
            additional_fields: answers
          })
        })
        if (response.ok) {
          this.activeStep += 1
          this.sendDataLayer('quiz_end')
          this.sendDataLayer('quiz_contact_method_step_passed')
        } else {
          const error = new Error()
          error.name = `[App.vue] finishQuiz ${response?.status}`
          error.status = response?.status
          const text = await response.text()
          error.message = text
          throw error
        }
      } catch (error) {
        let msg = {}
        try {
          msg = JSON.parse(error?.message)
        } catch (parseError) {
          msg = { raw: error?.message }
        }
        if (msg?.status?.client_code || msg?.status?.message) error.message = `Client code ${msg?.status?.client_code}: ${msg?.status?.message}`
        else if (msg.message) error.message = msg.message
        Sentry.captureException(error, {
          extra: {
            phone: this.phone.replace(/[^+\d]/g, ''),
            email: this.email,
            first_name: this.fname,
            last_name: this.lname,
            max_relationship_amount: this.quizAnswers['max_relationship_amount'],
            preferred_contact_method: this.quizAnswers['preferred_contact_method'],
            geo_place: this.location,
            iana_timezone: tzByLocation ? tzByLocation : Intl.DateTimeFormat().resolvedOptions().timeZone,
            form_submission_source_url: this.source_url ? `https://${this.source_url}` : window.location.href,
            session_fingerprint: this.fingerprint,
            additional_fields: answers,
            response: msg
          }
        })
      } finally {
        this.finishQuizDisable = false
      }
    },
    keepGoing() {
      const step = this.steps[this.activeStep]
      if (step.isKeepGoing) {
        this.showKeepGoing = true
        setTimeout(() => { this.showKeepGoing = false }, 3000)
      }
    },
    selectOption(key, val) {
      this.quizAnswers[key] = val
      if (key === 'preferred_contact_method') this.finishQuiz()
      else {
        setTimeout(() => {
          this.keepGoing()
          this.sendDataLayer(`quiz_${this.activeStep + 1}_step_passed`)
          this.activeStep += 1
        }, 400)
      }
    },
    nextStep() {
      this.keepGoing()
      this.sendDataLayer(`quiz_${this.activeStep + 1}_step_passed`)
      this.activeStep += 1
    },
    prevStep() {
      if (this.activeStep > 0) this.activeStep -= 1
    },
    contactsFilled(data) {
      this.fname = data.fname
      this.lname = data.lname
      this.email = data.email
      this.location = data.location
      this.sendDataLayer(`quiz_${this.activeStep + 1}_step_passed`)
      this.activeStep += 1
      this.sendDataLayer('quiz_contact_info_step_passed')
    },
    sendDataLayer(event) {
      if (window.dataLayer) {
        window.dataLayer.push({
          event,
          quiz_domain: this.quiz_domain ? this.quiz_domain : this.source_url ? this.source_url : null,
          quiz_url: this.quiz_url,
          quiz_placement: this.quiz_placement,
          email: this.email?.length ? this.email : null,
          phone_number: this.phone?.length ? this.phone : null
        })
      }
    }
  }
}
</script>
